/**
 * @lodex-pro/web-billing — App component (auth gate + tenant bootstrap + routed shell).
 *
 * Phase 4 / Plan 04-10 — auth gate skeleton.
 * Phase 12 / Task 12.3 — replaces the placeholder authenticated branch with the
 * real tenant-scoped AppShell + React Router, mirroring web-dispatch/src/App.tsx.
 *
 * Auth + tenant + role gates here are the SOLE React-level protection and are
 * UX-only — the API enforces Cognito JWT + role-guard + Postgres RLS (CLAUDE.md
 * three-layer security). This component makes NO trust decisions.
 *
 * Route structure:
 *   /             → PipelinePage (default — unbilled operators + Generate invoice)
 *   /pipeline/:customerKey                → PipelineCustomerPage (#50 — one customer's tickets)
 *   /pipeline/:customerKey/tickets/:jobId → BillingTicketPage (#50 — one ticket, read-only)
 *   /invoices     → InvoicesPage (generated invoices list)
 *   /invoices/:id → InvoiceDetailPage (header + line items + totals)
 *   /finance      → FinancePage (Phase 19 / Task 19.4 — the Finance dashboard; every
 *                   VIEW_INVOICES role, billing staff without dollars — server-enforced)
 *   /customers    → CustomersPage (gap 1, DEC-383 — every customer; search and open one)
 *   /customers/:id → CustomerPricesPage (gap 1 — the "Hauling & disposal prices"
 *                   tab; every VIEW_INVOICES role views, MANAGE_CUSTOMER_PRICES edits — API-enforced)
 *
 * Branding: product chrome (AppShell wordmark) says "lodeX Pro"; the invoice
 * detail surface renders the legal brand "Paelode Systems" (branding split).
 *
 * Spec: master spec §7; CLAUDE.md branding + three-layer security; DEC-004.
 */

import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import {
  Receipt,
  FileText,
  Clock,
  KeyRound,
  CalendarClock,
  PieChart,
  Droplets,
  Tags,
  Users,
} from 'lucide-react';
import {
  AppShell,
  TOASTER_OFFSET,
  useAuth,
  useActiveTenant,
  useApiOpts,
  useTenantBrand,
} from '@lodex-pro/web-shared';
import type { NavSection } from '@lodex-pro/web-shared';

import { SignInPage } from './auth/SignInPage';
import { TenantSelect } from './auth/TenantSelect';
import { PipelinePage } from './routes/PipelinePage';
import { PipelineCustomerPage } from './routes/PipelineCustomerPage';
import { BillingTicketPage } from './routes/BillingTicketPage';
import { InvoicesPage } from './routes/InvoicesPage';
import { InvoiceDetailPage } from './routes/InvoiceDetailPage';
import { AccountPage } from './routes/AccountPage';
import { AgingPage } from './routes/AgingPage';
import { FinancePage } from './routes/FinancePage';
import { PaymentTermsPage } from './routes/PaymentTermsPage';
import { DisposalPricesPage } from './routes/DisposalPricesPage';
import { CustomerDisposalFeesPage } from './routes/CustomerDisposalFeesPage';
import { CustomersPage } from './routes/CustomersPage';
import { CustomerPricesPage } from './routes/CustomerPricesPage';
import { useUserProfile } from './lib/use-user-timezone';
import { BillingNotificationBell } from './features/notifications/BillingNotificationBell';

/**
 * VIEW_INVOICES holders within a hauler tenant (ROLE_PERMISSIONS, permissions.ts).
 * Frontend gate is UX-only — the API enforces the real permission (3-layer security).
 */
const VIEW_INVOICES_ROLES: ReadonlySet<string> = new Set([
  'PLATFORM_ADMIN',
  'TENANT_ADMIN',
  'BILLING_MANAGER',
  'BILLING_STAFF',
  // DEC-313 — the combined office roles hold VIEW_INVOICES through their billing
  // half, so they must be able to OPEN this app. Without these four entries the
  // API would answer their requests and the frontend would still show them the
  // access-denied card.
  'DISPATCHER_BILLING_STAFF',
  'DISPATCHER_BILLING_MANAGER',
  'DISPATCH_MANAGER_BILLING_STAFF',
  'DISPATCH_MANAGER_BILLING_MANAGER',
]);

/**
 * MANAGE_PAYMENT_TERMS holders (DEC-326 item 7): tenant admin and billing manager, and
 * the combined roles whose billing half is a manager; NEVER billing staff. Mirrors
 * ROLE_PERMISSIONS, whose holder list is pinned in shared permissions.test.ts. UX only:
 * the API enforces the permission.
 */
const MANAGE_PAYMENT_TERMS_ROLES: ReadonlySet<string> = new Set([
  'TENANT_ADMIN',
  'BILLING_MANAGER',
  'DISPATCHER_BILLING_MANAGER',
  'DISPATCH_MANAGER_BILLING_MANAGER',
]);

/** MANAGE_DISPOSAL_PRICES holders in the billing app (Phase 28, DEC-331 D-9): the same
 *  roles as payment terms. UX only; the API enforces the permission. */
const MANAGE_DISPOSAL_PRICES_ROLES: ReadonlySet<string> = MANAGE_PAYMENT_TERMS_ROLES;

const DISPOSAL_PRICES_NAV: NavSection = {
  id: 'disposal-prices',
  label: 'Disposal prices',
  icon: <Droplets size={16} aria-hidden="true" />,
};

/** #52 (DEC-358) — what we CHARGE customers to dispose. Every billing role may view it;
 *  only MANAGE_DISPOSAL_PRICES holders get the edit controls (UX only; the API enforces). */
const DISPOSAL_FEES_NAV: NavSection = {
  id: 'disposal-fees',
  label: 'Customer disposal fees',
  icon: <Tags size={16} aria-hidden="true" />,
};

/** Gap 1 (DEC-383) — each customer's hauling and disposal prices. Every billing role may view
 *  them; the page asks the API whether this user may edit (UX only; the API enforces). */
const CUSTOMER_PRICES_NAV: NavSection = {
  id: 'customer-prices',
  label: 'Customer prices',
  icon: <Users size={16} aria-hidden="true" />,
};

const PAYMENT_TERMS_NAV: NavSection = {
  id: 'payment-terms',
  label: 'Payment terms',
  icon: <CalendarClock size={16} aria-hidden="true" />,
};

/** Navigation sections for the billing console. */
const BILLING_NAV: readonly NavSection[] = [
  { id: 'pipeline', label: 'Pipeline', icon: <Receipt size={16} aria-hidden="true" /> },
  { id: 'invoices', label: 'Invoices', icon: <FileText size={16} aria-hidden="true" /> },
  { id: 'aging', label: 'Aging', icon: <Clock size={16} aria-hidden="true" /> },
  // Phase 19 / Task 19.4 (DEC-325 D-1) — beside Aging.
  { id: 'finance', label: 'Finance', icon: <PieChart size={16} aria-hidden="true" /> },
  // Quick 260827-pwd — self-service password change.
  { id: 'account', label: 'Account', icon: <KeyRound size={16} aria-hidden="true" /> },
];

/** Maps route pathname to nav section id. */
function pathnameToNavId(pathname: string): string {
  if (pathname.startsWith('/invoices')) return 'invoices';
  if (pathname.startsWith('/aging')) return 'aging';
  if (pathname.startsWith('/finance')) return 'finance';
  if (pathname.startsWith('/payment-terms')) return 'payment-terms';
  if (pathname.startsWith('/disposal-prices')) return 'disposal-prices';
  if (pathname.startsWith('/disposal-fees')) return 'disposal-fees';
  if (pathname.startsWith('/customers')) return 'customer-prices';
  return 'pipeline';
}

/** Maps nav section id to route pathname. */
function navIdToPathname(id: string): string {
  switch (id) {
    case 'invoices':
      return '/invoices';
    case 'aging':
      return '/aging';
    case 'finance':
      return '/finance';
    case 'payment-terms':
      return '/payment-terms';
    case 'disposal-prices':
      return '/disposal-prices';
    case 'disposal-fees':
      return '/disposal-fees';
    case 'customer-prices':
      return '/customers';
    case 'account':
      return '/account';
    default:
      return '/';
  }
}

/** Maps route pathname to the AppShell top-bar page title. */
function pathnameToPageTitle(pathname: string): string {
  if (pathname.startsWith('/invoices')) return 'Invoices';
  if (pathname.startsWith('/aging')) return 'Aging';
  if (pathname.startsWith('/finance')) return 'Finance';
  if (pathname.startsWith('/payment-terms')) return 'Payment terms';
  if (pathname.startsWith('/disposal-prices')) return 'Disposal prices';
  if (pathname.startsWith('/disposal-fees')) return 'Customer disposal fees';
  if (pathname.startsWith('/customers')) return 'Customer prices';
  if (pathname.startsWith('/account')) return 'Account';
  return 'Pipeline';
}

/** Inner routed shell — must be inside BrowserRouter to use useLocation/useNavigate. */
function BillingShell(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOutAll } = useAuth();

  const { data: profileData } = useUserProfile();
  const role = profileData?.profile.role ?? '';
  const canViewBilling = VIEW_INVOICES_ROLES.has(role);
  const canManageTerms = MANAGE_PAYMENT_TERMS_ROLES.has(role);
  const canManageDisposalPrices = MANAGE_DISPOSAL_PRICES_ROLES.has(role);
  // Payment terms and disposal prices sit just before Account, only for the roles that may use them.
  const extras = [
    DISPOSAL_FEES_NAV,
    CUSTOMER_PRICES_NAV,
    ...(canManageTerms ? [PAYMENT_TERMS_NAV] : []),
    ...(canManageDisposalPrices ? [DISPOSAL_PRICES_NAV] : []),
  ];
  const nav = [...BILLING_NAV.slice(0, -1), ...extras, ...BILLING_NAV.slice(-1)];

  const activeNavId = pathnameToNavId(location.pathname);
  const pageTitle = pathnameToPageTitle(location.pathname);

  const handleNavigate = React.useCallback(
    (id: string): void => {
      void navigate(navIdToPathname(id));
    },
    [navigate],
  );

  // Identity from the SERVER profile (source of truth) — not the Amplify client
  // object, whose signInDetails.loginId is absent on a restored session.
  const userName =
    profileData?.profile.fullName ??
    profileData?.profile.email ??
    user?.signInDetails?.loginId ??
    user?.username ??
    'Unknown user';

  // Quick 261013-srg PR 0 — the company's real name and logo for the sidebar header
  // (was the 'Hauler Tenant' placeholder). Initials show until a logo is uploaded.
  const { tenantName, logo: tenantLogo } = useTenantBrand();

  return (
    <AppShell
      nav={canViewBilling ? nav : []}
      activeNavId={activeNavId}
      onNavigate={handleNavigate}
      pageTitle={pageTitle}
      tenantName={tenantName}
      tenantLogo={tenantLogo}
      userName={userName}
      role={role}
      onSignOut={(): void => {
        void signOutAll();
      }}
      // OPEN-085 follow-up — only for users who can open billing pages: every link in the
      // bell leads to one, and the API answers GET /v1/notifications for any role anyway.
      headerRight={canViewBilling ? <BillingNotificationBell /> : undefined}
    >
      {canViewBilling ? (
        <Routes>
          <Route path="/" element={<PipelinePage />} />
          {/* #50 — one customer's unbilled tickets, and one ticket. */}
          <Route path="/pipeline/:customerKey" element={<PipelineCustomerPage />} />
          <Route path="/pipeline/:customerKey/tickets/:jobId" element={<BillingTicketPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
          <Route path="/aging" element={<AgingPage />} />
          <Route path="/finance" element={<FinancePage />} />
          {canManageTerms && <Route path="/payment-terms" element={<PaymentTermsPage />} />}
          {canManageDisposalPrices && (
            <Route path="/disposal-prices" element={<DisposalPricesPage />} />
          )}
          <Route
            path="/disposal-fees"
            element={<CustomerDisposalFeesPage canEdit={canManageDisposalPrices} />}
          />
          {/* Gap 1 (DEC-383) — the customer list and one customer's prices tab. */}
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:haulerOperatorId" element={<CustomerPricesPage />} />
          {/* Quick 260827-pwd — self-service password change. */}
          <Route path="/account" element={<AccountPage />} />
        </Routes>
      ) : (
        <div style={{ padding: '48px', maxWidth: '520px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>
            You don&apos;t have access to billing
          </h2>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Your role doesn&apos;t include billing permissions in this workspace. Contact your
            administrator if you believe this is a mistake.
          </p>
        </div>
      )}
    </AppShell>
  );
}

export function App(): React.ReactElement {
  const { isAuthenticated, loading } = useAuth();
  const apiOpts = useApiOpts();

  // Tenant bootstrap — runs ONLY once authenticated. A user whose
  // active_tenant_id is NULL would otherwise reach the shell and 403 on every
  // tenant-scoped call. Sole-membership users are auto-selected; multi pick.
  const { state: tenantState, selectTenant } = useActiveTenant(
    apiOpts,
    isAuthenticated && !loading,
  );

  if (loading) {
    return <p role="status">Loading…</p>;
  }
  if (!isAuthenticated) {
    return <SignInPage />;
  }

  // Authenticated — resolve the active tenant before mounting the shell.
  if (tenantState.kind === 'checking') {
    return <p role="status">Loading workspace…</p>;
  }
  if (tenantState.kind === 'select') {
    return (
      <TenantSelect
        memberships={tenantState.memberships}
        onSelect={(tenantId): void => {
          void selectTenant(tenantId);
        }}
      />
    );
  }
  if (tenantState.kind === 'none') {
    return (
      <main className="auth-shell" aria-label="No workspace">
        <div className="auth-card">
          <h1 className="auth-brand">Paelode Systems Billing</h1>
          <p className="auth-subtitle">
            Your account isn&apos;t assigned to a workspace yet. Contact your administrator.
          </p>
        </div>
      </main>
    );
  }
  // Quick 260816-vr9 — a revoked member is NOT a new user. Both arrive here with
  // an empty membership list, so the 'none' copy above used to cover both and
  // told someone who had just been deactivated that they were never set up —
  // advice they cannot act on. This branch is the whole user-visible point of
  // the ACCESS_REVOKED work; without it the API-side fix (PR #225) is inert.
  // "workspace" rather than the catalog's "tenant": these apps never say tenant.
  if (tenantState.kind === 'revoked') {
    return (
      <main className="auth-shell" aria-label="Access removed">
        <div className="auth-card">
          <h1 className="auth-brand">Paelode Systems Billing</h1>
          <p className="auth-subtitle">
            Your access to this workspace has been removed. Contact your administrator.
          </p>
        </div>
      </main>
    );
  }
  // DEC-349 (OPEN-134) — the company is suspended and the user has no other one.
  // Before this branch the sole-membership auto-switch put them into an empty app
  // as "Unknown user". Copy matches the API's TENANT_SUSPENDED message.
  if (tenantState.kind === 'suspended') {
    return (
      <main className="auth-shell" aria-label="Company suspended">
        <div className="auth-card">
          <h1 className="auth-brand">Paelode Systems Billing</h1>
          <p className="auth-subtitle">
            This company&apos;s account is suspended. Contact your administrator.
          </p>
        </div>
      </main>
    );
  }
  if (tenantState.kind === 'error') {
    return (
      <main className="auth-shell" aria-label="Workspace error">
        <div className="auth-card">
          <h1 className="auth-brand">Paelode Systems Billing</h1>
          <p className="auth-subtitle">Couldn&apos;t load your workspace. Refresh to try again.</p>
        </div>
      </main>
    );
  }

  // tenantState.kind === 'ready'
  return (
    <BrowserRouter>
      {/* Sonner toast container — top-right, mounted once here. */}
      <Toaster position="top-right" theme="dark" offset={TOASTER_OFFSET} />
      <BillingShell />
    </BrowserRouter>
  );
}
